import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/lib/auth-context'
import { Spinner } from '@/components/ui'

/**
 * AuthGate — guards authenticated routes.
 *
 * Shows a warm parchment loading screen while auth state resolves.
 * Unauthenticated users are sent to the login gate, which explains the
 * sign-in and returns them to the originally requested path afterwards.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading, isNewUser } = useAuth()
  const { t } = useTranslation()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="md" />
          <p className="font-body text-sm text-quiet-text animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <Navigate
        to={`/login-gate?from=${encodeURIComponent(location.pathname)}`}
        replace
      />
    )
  }

  // Users who haven't finished onboarding can't skip it by navigating
  // directly to an authenticated route — send them to the first step.
  if (isNewUser) {
    return <Navigate to="/onboarding/manifesto" replace />
  }

  return <>{children}</>
}
